import {
  Discipline,
  Event,
  EventPageData,
  EventType,
  Route,
} from '@/apiTypes/event';
import {
  createReminderSchedule,
  updateReminderSchedulesForEvent,
  deleteReminderSchedulesForEvent,
} from '@/helpers/eventBridgeScheduler.server';
import {Registration} from '@/apiTypes/registration';
import {SAVED_EVENTS_LIST_NAME} from '@/server/savedEvent';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {
  authMiddleware,
  optionalAuthMiddleware,
} from '@/helpers/authMiddleware';
import {parseGpxFromUrl} from '@/helpers/parseGpx.server';
import {prismaClient} from '@/helpers/prismaClient.server';
import {syncEventTagsInTx, TagNamesSchema} from '@/server/eventTags';
import {
  requireClaimableEventAccess,
  requireEventAccess,
} from '@/helpers/requireAuth.server';
import {resolveEventTimezone} from '@/helpers/resolveEventTimezone.server';
import {
  sanitizeDescription,
  stripHtmlToText,
} from '@/helpers/sanitizeHtml.server';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {Prisma} from '@wayra/database';
import {z} from 'zod';

// Input validation schemas
const GetEventByIdInput = z.string().uuid();
const DeleteEventInput = z.string().uuid();

const CreateEventInput = z.object({
  organizerId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  eventType: EventType,
  discipline: Discipline,
  sourceUrl: z.string().nullable(),
  heroUrl: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  location: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  routes: z.array(
    z.object({
      tempId: z.string(),
      name: z.string().min(1),
      description: z.string().nullable(),
      distanceMeters: z.number().nullable(),
      elevationGainMeters: z.number().nullable(),
      routeFileUrl: z.string().nullable(),
    })
  ),
  categories: z.array(
    z.object({
      tempId: z.string(),
      name: z.string().min(1),
      description: z.string().nullable(),
      startTime: z.string().nullable(),
      maxParticipants: z.number().nullable(),
      routeTempId: z.string().nullable(),
      price: z.object({
        amount: z.number().int().min(0),
        currency: z.string(),
        endsAt: z.string().nullable(),
      }),
    })
  ),
  tagNames: TagNamesSchema.optional(),
});

const UpdateEventInput = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  editorialContent: z.string().nullable().optional(),
  eventType: EventType,
  discipline: Discipline,
  governingBody: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  location: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  heroUrl: z.string().nullable(),
  requiresClubMembership: z.boolean(),
  tagNames: TagNamesSchema.optional(),
});

const ListEventsInput = z.object({
  limit: z.number().min(1).max(100).default(20),
  offset: z.number().min(0).default(0),
  eventType: z.enum(['route', 'race', 'ride']).optional(),
  discipline: z
    .enum([
      'road',
      'gravel',
      'mountain',
      'crit',
      'cyclocross',
      'track',
      'triathlon',
      'ultra',
      'other',
    ])
    .optional(),
  startDateFrom: z.string().datetime().optional(),
  startDateTo: z.string().datetime().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  radiusKm: z.number().optional(),
  search: z.string().optional(),
});

export type ListEventsParams = z.infer<typeof ListEventsInput>;

/** Shared Prisma include for fetching a full event with relations.
 * Includes ALL tags (system + free-form, public + internal) — used by the
 * organizer-side editor where the admin needs to see every assignment. */
const eventInclude = {
  organizer: true,
  editorialAuthor: {
    select: {firstName: true, avatarUrl: true},
  },
  routes: {
    orderBy: {createdAt: 'asc' as const},
    omit: {track_points: true, waypoints: true},
    include: {
      categories: {
        orderBy: {createdAt: 'asc' as const},
        include: {
          prices: {orderBy: {endsAt: {sort: 'asc' as const, nulls: 'last'}}},
        },
      },
    },
  },
  categories: {
    orderBy: {createdAt: 'asc' as const},
    include: {
      prices: {orderBy: {endsAt: {sort: 'asc' as const, nulls: 'last'}}},
    },
  },
  prerequisites: {
    include: {
      prerequisiteEvent: {
        select: {id: true, name: true, startDate: true},
      },
    },
  },
  tags: {
    include: {tag: true},
  },
};

/** Like `eventInclude` but only includes public-visible tags. Used by
 * public read paths (event detail page, scraper-facing reads). */
const publicEventInclude = {
  ...eventInclude,
  tags: {
    where: {tag: {isPublic: true}},
    include: {tag: true},
  },
};

/** Maps a raw event-with-tag-assignments into the shape `Event.parse`
 * expects: tags as a flat array of EventTag instead of EventTagAssignment
 * rows. Tolerates missing `tags` for callers that don't include them. */
function flattenEventTags<T extends {tags?: Array<{tag: unknown}>}>(
  event: T,
): Omit<T, 'tags'> & {tags: unknown[]} {
  return {
    ...event,
    tags: (event.tags ?? []).map((a) => a.tag),
  };
}

/**
 * Get a single event by ID with all relations
 */
export const getEventById = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): string => {
    const result = GetEventByIdInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId}) => {
    const db = await prismaClient();

    const event = await db.event.findUnique({
      where: {id: eventId},
      include: eventInclude,
    });

    if (!event) {
      throw new Error('Event not found');
    }

    return Event.parse(flattenEventTags(event));
  });

/**
 * Consolidated event page data loader. Fetches the event with all relations
 * plus user-specific context (registration, attendance, prerequisites,
 * participants, organizer admin status) in a single server call.
 */
export const getEventPageData = createServerFn({
  method: 'GET',
})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = GetEventByIdInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    const auth = context.auth;
    const db = await prismaClient();

    const event = await db.event.findUnique({
      where: {id: eventId},
      include: publicEventInclude,
    });

    if (!event) {
      throw new Error('Event not found');
    }

    const parsedEvent = Event.parse(flattenEventTags(event));
    const profileId = auth?.profile.id ?? null;
    const organizerId = event.organizerId;
    const prerequisiteIds = (event.prerequisites ?? []).map(
      (p) => p.prerequisiteEventId
    );

    const [
      registration,
      attendance,
      prereqRegistrations,
      participantRegistrations,
      participantCount,
      adminCount,
      organizerAdminMembership,
      organizerMembership,
      savedEvent,
    ] = await Promise.all([
      profileId
        ? db.registration.findFirst({
            where: {
              profileId,
              eventId,
              status: {in: ['APPROVED', 'PENDING_PREREQUISITE_REVIEW', 'PAID']},
            },
            orderBy: {createdAt: 'desc'},
          })
        : Promise.resolve(null),
      profileId
        ? db.eventAttendance.findUnique({
            where: {profileId_eventId: {profileId, eventId}},
          })
        : Promise.resolve(null),
      profileId && prerequisiteIds.length > 0
        ? db.registration.findMany({
            where: {profileId, eventId: {in: prerequisiteIds}},
            select: {eventId: true},
          })
        : Promise.resolve([]),
      db.registration.findMany({
        where: {
          eventId,
          status: {in: ['APPROVED', 'PAID']},
        },
        take: 8,
        orderBy: {createdAt: 'desc'},
        select: {
          id: true,
          createdAt: true,
          profile: {
            select: {
              id: true,
              firstName: true,
              lastName: true,
              displayName: true,
              avatarUrl: true,
            },
          },
        },
      }),
      db.registration.count({
        where: {eventId, status: {in: ['APPROVED', 'PAID']}},
      }),
      organizerId
        ? db.organizerAdmin.count({where: {organizerId}})
        : Promise.resolve(0),
      profileId && organizerId
        ? db.organizerAdmin.findFirst({
            where: {profileId, organizerId},
            select: {id: true},
          })
        : Promise.resolve(null),
      profileId && event.requiresClubMembership && event.organizerId
        ? db.organizerMember.findFirst({
            where: {
              profileId,
              organizerId: event.organizerId,
              leftAt: null,
            },
            select: {id: true},
          })
        : Promise.resolve(null),
      profileId
        ? db.eventListItem.findFirst({
            where: {
              eventId,
              eventList: {
                profileId,
                name: SAVED_EVENTS_LIST_NAME,
              },
            },
          })
        : Promise.resolve(null),
    ]);

    const registrationStatus = registration
      ? {isRegistered: true, registration: Registration.parse(registration)}
      : {isRegistered: false, registration: null};

    const attendanceStatus = {isAttending: !!attendance};
    const savedStatus = {isSaved: !!savedEvent};

    const satisfiedIds = new Set(prereqRegistrations.map((r) => r.eventId));
    const prerequisites = (event.prerequisites ?? []).map((p) => ({
      id: p.prerequisiteEventId,
      name: p.prerequisiteEvent.name,
      satisfied: profileId ? satisfiedIds.has(p.prerequisiteEventId) : null,
    }));
    const prerequisiteStatus = {
      prerequisites,
      allSatisfied:
        prerequisites.length === 0
          ? null
          : profileId
            ? prerequisites.every((p) => p.satisfied)
            : null,
    };

    const participants = {
      participants: participantRegistrations.map((r) => ({
        id: r.id,
        registeredAt: r.createdAt,
        profile: r.profile,
      })),
      totalCount: participantCount,
    };

    const organizerAdminStatus = {
      isAdmin: !!organizerAdminMembership,
      isClaimable: adminCount === 0,
    };

    const membershipStatus = {isMember: !!organizerMembership};

    return EventPageData.parse({
      event: parsedEvent,
      shareDescription: stripHtmlToText(parsedEvent.description),
      registrationStatus,
      attendanceStatus,
      savedStatus,
      prerequisiteStatus,
      participants,
      organizerAdminStatus,
      membershipStatus,
    });
  });

/**
 * Fetch track_points and waypoints for all routes of an event.
 * Designed for lazy-loading after the initial event page render.
 */
export const getRouteTracksForEvent = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): string => {
    const result = GetEventByIdInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId}) => {
    const db = await prismaClient();

    const routes = await db.route.findMany({
      where: {eventId},
      select: {
        id: true,
        track_points: true,
        waypoints: true,
      },
    });

    return z
      .array(
        Route.pick({
          id: true,
          track_points: true,
          waypoints: true,
        })
      )
      .parse(routes);
  });

/**
 * Get a single event by external ID (e.g., "bikereg:12345")
 */
export const getEventByExternalId = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): string => {
    if (typeof data !== 'string' || !data) {
      throw new Error('External ID must be a non-empty string');
    }
    return data;
  })
  .handler(async ({data: externalId}) => {
    const db = await prismaClient();

    const event = await db.event.findFirst({
      where: {externalId},
      include: publicEventInclude,
    });

    if (!event) {
      throw new Error('Event not found');
    }

    return Event.parse(flattenEventTags(event));
  });

/**
 * List events with filtering and pagination
 */
export const listEvents = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): ListEventsParams => {
    const result = ListEventsInput.safeParse(data ?? {});
    if (!result.success) {
      throw new Error(
        result.error.errors.map((e) => e.message).join(', ') || 'Invalid input'
      );
    }
    return result.data;
  })
  .handler(async ({data: params}) => {
    const db = await prismaClient();

    // Build where clause
    const where: Prisma.EventWhereInput = {
      archived: false,
    };

    if (params.eventType) {
      where.eventType = params.eventType;
    }

    if (params.discipline) {
      where.discipline = params.discipline;
    }

    if (params.startDateFrom || params.startDateTo) {
      where.startDate = {};
      if (params.startDateFrom) {
        where.startDate.gte = new Date(params.startDateFrom);
      }
      if (params.startDateTo) {
        where.startDate.lte = new Date(params.startDateTo);
      }
    }

    if (params.search) {
      where.OR = [
        {name: {contains: params.search, mode: 'insensitive'}},
        {description: {contains: params.search, mode: 'insensitive'}},
        {
          organizer: {name: {contains: params.search, mode: 'insensitive'}},
        },
      ];
    }

    // Get total count for pagination
    const total = await db.event.count({where});

    // Fetch events
    const events = await db.event.findMany({
      where,
      include: {
        organizer: true,
        routes: true,
        categories: {
          include: {
            prices: true,
          },
        },
      },
      orderBy: {
        startDate: 'asc',
      },
      take: params.limit,
      skip: params.offset,
    });

    return {
      events: z.array(Event).parse(events),
      pagination: {
        total,
        limit: params.limit,
        offset: params.offset,
        hasMore: params.offset + events.length < total,
      },
    };
  });

/**
 * List upcoming events (convenience function)
 */
export const listUpcomingEvents = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): {limit?: number} => {
    if (data && typeof data === 'object' && 'limit' in data) {
      const limit = (data as {limit: unknown}).limit;
      if (typeof limit === 'number' && limit > 0 && limit <= 100) {
        return {limit};
      }
    }
    return {limit: 20};
  })
  .handler(async ({data: params}) => {
    const db = await prismaClient();

    const events = await db.event.findMany({
      where: {
        archived: false,
        startDate: {
          gte: new Date(),
        },
      },
      include: {
        organizer: true,
        routes: true,
        categories: {
          include: {
            prices: true,
          },
        },
      },
      orderBy: {
        startDate: 'asc',
      },
      take: params.limit,
    });

    return z.array(Event).parse(events);
  });

/**
 * Get events by organizer
 */
export const getEventsByOrganizer = createServerFn({
  method: 'GET',
})
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({data: organizerId}) => {
    const db = await prismaClient();

    const events = await db.event.findMany({
      where: {organizerId},
      include: {
        organizer: true,
        routes: true,
        categories: {
          include: {
            prices: true,
          },
        },
      },
      orderBy: {
        startDate: 'asc',
      },
    });

    return z.array(Event).parse(events);
  });

/**
 * Get event statistics (for admin/dashboard)
 */
export const getEventStats = createServerFn({
  method: 'GET',
}).handler(async () => {
  const db = await prismaClient();

  const [totalEvents, totalOrganizers, eventsByType, upcomingCount] =
    await Promise.all([
      db.event.count(),
      db.organizer.count(),
      db.event.groupBy({
        by: ['eventType'],
        _count: true,
      }),
      db.event.count({
        where: {
          startDate: {
            gte: new Date(),
          },
        },
      }),
    ]);

  return {
    totalEvents,
    totalOrganizers,
    upcomingCount,
    eventsByType: eventsByType.reduce(
      (acc, item) => {
        acc[item.eventType] = item._count;
        return acc;
      },
      {} as Record<string, number>
    ),
  };
});

/**
 * Checks whether the current user has edit access to an event.
 * Returns true for superadmins or members of the event's organizer.
 */
export const checkEventEditAccess = createServerFn({method: 'GET'})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    const auth = context.auth;
    if (!auth) {
      return {hasAccess: false};
    }

    if (auth.profile.level === 'superadmin') {
      return {hasAccess: true};
    }

    const db = await prismaClient();
    const event = await db.event.findUnique({
      where: {id: eventId},
      select: {organizerId: true},
    });

    if (!event?.organizerId) {
      return {hasAccess: false};
    }

    const isClaimable =
      (await db.organizerAdmin.count({
        where: {organizerId: event.organizerId},
      })) === 0;

    if (isClaimable) {
      return {hasAccess: true};
    }

    const membership = await db.organizerAdmin.findFirst({
      where: {
        profileId: auth.profile.id,
        organizerId: event.organizerId,
      },
    });

    return {hasAccess: !!membership};
  });

/**
 * Update an event's fields. If the event is claimable (organizer has no
 * admins), the current user is automatically added as an admin of that
 * organizer in the same transaction.
 */
export const updateEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof UpdateEventInput> => {
    const result = UpdateEventInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const auth = await requireClaimableEventAccess(data.id, context.auth);
    const isSuperAdmin = auth.profile.level === 'superadmin';
    const db = await prismaClient();

    const newStartDate = data.startDate ? new Date(data.startDate) : null;

    // Fetch existing event to compare location/date fields
    const existing = await db.event.findUnique({
      where: {id: data.id},
      select: {
        organizerId: true,
        startDate: true,
        lat: true,
        lng: true,
        timezone: true,
      },
    });

    // Only resolve timezone when location or date actually changed
    const locationOrDateChanged =
      existing?.lat !== data.lat ||
      existing?.lng !== data.lng ||
      existing?.startDate?.getTime() !== newStartDate?.getTime();

    const timezone = locationOrDateChanged && newStartDate
      ? await resolveEventTimezone(data.lat, data.lng, newStartDate)
      : (existing?.timezone ?? null);

    if (!existing?.organizerId) {
      throw new Error('Event has no organizer');
    }

    const previousStartDate = await db.$transaction(async (tx) => {

      const isClaimable =
        (await tx.organizerAdmin.count({
          where: {organizerId: existing.organizerId},
        })) === 0;

      await tx.event.update({
        where: {id: data.id},
        data: {
          name: data.name,
          description: sanitizeDescription(data.description),
          eventType: data.eventType,
          discipline: data.discipline,
          governingBody: data.governingBody,
          sourceUrl: data.sourceUrl,
          lat: data.lat,
          lng: data.lng,
          location: data.location,
          startDate: newStartDate,
          endDate: data.endDate ? new Date(data.endDate) : null,
          timezone,
          heroUrl: data.heroUrl,
          requiresClubMembership: data.requiresClubMembership,
          ...(isSuperAdmin && data.editorialContent !== undefined && (() => {
            const sanitized = data.editorialContent
              ? sanitizeDescription(data.editorialContent)
              : data.editorialContent;
            return {
              editorialContent: sanitized,
              editorialAuthorId: sanitized ? auth.profile.id : null,
            };
          })()),
          // Superadmin edits don't bump editedAt — keeps the column as a
          // "last edited by a real organizer" signal for audit purposes.
          ...(!isSuperAdmin && {editedAt: new Date()}),
        },
      });

      // Superadmins should never be auto-claimed as organizer owners
      if (isClaimable && !isSuperAdmin) {
        await tx.organizerAdmin.create({
          data: {
            profileId: auth.profile.id,
            organizerId: existing.organizerId,
          },
        });
      }

      if (data.tagNames !== undefined) {
        await syncEventTagsInTx(
          tx,
          data.id,
          existing.organizerId,
          data.tagNames,
        );
      }

      return existing.startDate;
    });

    // Update reminders if startDate actually changed
    const oldMs = previousStartDate?.getTime() ?? null;
    const newMs = data.startDate ? new Date(data.startDate).getTime() : null;
    if (oldMs !== newMs) {
      if (data.startDate) {
        const newStartDate = new Date(data.startDate);
        const fireAt = new Date(newStartDate.getTime() - 24 * 60 * 60 * 1000);
        await updateReminderSchedulesForEvent({
          eventId: data.id,
          fireAt,
        });

        // Re-set reminders for registrations whose category has a startTime,
        // since those should fire relative to the category time, not event date.
        const registrations = await db.registration.findMany({
          where: {
            eventId: data.id,
            category: {startTime: {not: null}},
          },
          select: {
            profileId: true,
            eventId: true,
            category: {select: {startTime: true}},
          },
        });

        for (const reg of registrations) {
          if (!reg.category?.startTime) continue;
          const categoryFireAt = new Date(
            reg.category.startTime.getTime() - 24 * 60 * 60 * 1000,
          );
          createReminderSchedule({
            profileId: reg.profileId,
            eventId: reg.eventId,
            type: 'REGISTRATION',
            fireAt: categoryFireAt,
          }).catch((err) =>
            console.error(
              'Failed to restore category-based reminder:',
              err,
            ),
          );
        }
      } else {
        deleteReminderSchedulesForEvent({
          eventId: data.id,
        }).catch((err) =>
          console.error('Failed to delete event reminders:', err),
        );
      }
    }

    return {success: true};
  });

/**
 * Delete an event by ID
 */
export const deleteEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = DeleteEventInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    await requireClaimableEventAccess(eventId, context.auth);
    const db = await prismaClient();
    // Delete all EventBridge schedules before cascade-deleting DB records
    await deleteReminderSchedulesForEvent({ eventId });
    await db.event.delete({where: {id: eventId}});
    return {success: true};
  });

/**
 * Create a new event with routes, categories, and pricing in a single transaction.
 */
export const createEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof CreateEventInput> => {
    const result = CreateEventInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    if (profile.level !== 'superadmin') {
      const membership = await db.organizerAdmin.findFirst({
        where: {
          profileId: profile.id,
          organizerId: data.organizerId,
        },
      });
      if (!membership) {
        throw new Error('Not authorized for this organizer');
      }
    }

    // Parse GPX files before the transaction to avoid holding the DB connection during network I/O
    const gpxResults = await Promise.all(
      data.routes.map(async (route) => {
        if (!route.routeFileUrl) return null;
        try {
          return await parseGpxFromUrl(route.routeFileUrl);
        } catch {
          return null;
        }
      })
    );

    // Resolve timezone from coordinates before the transaction
    const startDate = data.startDate ? new Date(data.startDate) : null;
    const timezone =
      startDate ? await resolveEventTimezone(data.lat, data.lng, startDate) : null;

    const event = await db.$transaction(async (tx) => {
      const created = await tx.event.create({
        data: {
          name: data.name,
          description: sanitizeDescription(data.description),
          eventType: data.eventType,
          discipline: data.discipline,
          sourceUrl: data.sourceUrl,
          heroUrl: data.heroUrl,
          lat: data.lat,
          lng: data.lng,
          location: data.location,
          startDate,
          endDate: data.endDate ? new Date(data.endDate) : null,
          timezone,
          organizerId: data.organizerId,
        },
      });

      const routeIdMap = new Map<string, string>();
      for (let i = 0; i < data.routes.length; i++) {
        const route = data.routes[i];
        const gpxData = gpxResults[i];

        let distanceMeters = route.distanceMeters;
        let elevationGainMeters = route.elevationGainMeters;
        let trackPoints = null;
        let waypoints = null;

        if (gpxData) {
          trackPoints =
            gpxData.trackPoints.length > 0 ? gpxData.trackPoints : null;
          waypoints = gpxData.waypoints.length > 0 ? gpxData.waypoints : null;
          if (distanceMeters === null && gpxData.distanceMeters !== null) {
            distanceMeters = gpxData.distanceMeters;
          }
          if (
            elevationGainMeters === null &&
            gpxData.elevationGainMeters !== null
          ) {
            elevationGainMeters = gpxData.elevationGainMeters;
          }
        }

        const createdRoute = await tx.route.create({
          data: {
            name: route.name,
            description: route.description,
            distance_meters: distanceMeters,
            elevation_gain_meters: elevationGainMeters,
            routeFileUrl: route.routeFileUrl || null,
            track_points: trackPoints,
            waypoints,
            eventId: created.id,
          },
        });
        routeIdMap.set(route.tempId, createdRoute.id);
      }

      for (const category of data.categories) {
        const routeId = category.routeTempId
          ? (routeIdMap.get(category.routeTempId) ?? null)
          : null;

        const createdCategory = await tx.category.create({
          data: {
            name: category.name,
            description: category.description,
            startTime: category.startTime ? new Date(category.startTime) : null,
            maxParticipants: category.maxParticipants,
            isOpen: true,
            isSoldOut: false,
            sortOrder: 0,
            eventId: created.id,
            routeId,
          },
        });

        if (category.price.amount >= 0) {
          await tx.categoryPrice.create({
            data: {
              amount: category.price.amount,
              currency: category.price.currency,
              endsAt: category.price.endsAt
                ? new Date(category.price.endsAt)
                : null,
              categoryId: createdCategory.id,
            },
          });
        }
      }

      if (data.tagNames !== undefined) {
        await syncEventTagsInTx(tx, created.id, data.organizerId, data.tagNames);
      }

      return created;
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'Event Created',
      {
        event_id: event.id,
        event_name: data.name,
        event_type: data.eventType,
        discipline: data.discipline,
      },
      requestContext
    );

    return {success: true, eventId: event.id};
  });

const ArchiveEventInput = z.string().uuid();

/**
 * Archives an event, hiding it from public-facing queries.
 * Requires claimable event access (organizer admin or superadmin).
 */
export const archiveEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = ArchiveEventInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    await requireClaimableEventAccess(eventId, context.auth);
    const db = await prismaClient();
    await db.event.update({
      where: {id: eventId},
      data: {archived: true},
    });
    return {success: true};
  });

/**
 * Restores an archived event, making it visible in public queries again.
 * Requires claimable event access (organizer admin or superadmin).
 */
export const restoreEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = ArchiveEventInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    await requireClaimableEventAccess(eventId, context.auth);
    const db = await prismaClient();
    await db.event.update({
      where: {id: eventId},
      data: {archived: false},
    });
    return {success: true};
  });

const CloneEventInput = z.object({
  eventId: z.string().uuid(),
  newStartDate: z.string().datetime(),
});

/**
 * Shifts a date by a fixed number of milliseconds. Returns null if the
 * input is null. Used by cloneEvent to keep relative timing of endDate,
 * Category.startTime, and CategoryPrice.endsAt aligned with the new
 * event start.
 */
function shiftDate(date: Date | null, offsetMs: number): Date | null {
  return date ? new Date(date.getTime() + offsetMs) : null;
}

/**
 * Clones an event with a new start date. Copies core event fields,
 * routes, categories (with remapped routeIds), prices, and prerequisites
 * in both directions. Skips participant- and tracking-state
 * (registrations, attendances, reminders, list saves, comments).
 *
 * Date-bearing fields (endDate, Category.startTime, CategoryPrice.endsAt)
 * shift by (newStartDate - sourceStartDate). If the source has no
 * startDate, the offset is 0 and only newStartDate is set on the clone.
 *
 * Auth: requires `requireEventAccess` — superadmin OR OrganizerAdmin
 * of the source event's organizer. Unclaimed events are clonable
 * only by superadmins.
 */
export const cloneEvent = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof CloneEventInput> => {
    const result = CloneEventInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid clone input');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireEventAccess(data.eventId, context.auth);

    const db = await prismaClient();
    const newStartDate = new Date(data.newStartDate);

    const result = await db.$transaction(async (tx) => {
      const source = await tx.event.findUniqueOrThrow({
        where: {id: data.eventId},
        include: {
          routes: true,
          categories: {include: {prices: true}},
          prerequisites: true,
          prerequisiteOf: true,
        },
      });

      const offsetMs = source.startDate
        ? newStartDate.getTime() - source.startDate.getTime()
        : 0;

      const newEvent = await tx.event.create({
        data: {
          name: source.name,
          description: source.description,
          location: source.location,
          eventType: source.eventType,
          discipline: source.discipline,
          governingBody: source.governingBody,
          sourceUrl: source.sourceUrl,
          heroUrl: source.heroUrl,
          lat: source.lat,
          lng: source.lng,
          organizerId: source.organizerId,
          skipRiderFields: source.skipRiderFields,
          archived: false,
          editedAt: null,
          externalId: null,
          startDate: newStartDate,
          endDate: shiftDate(source.endDate, offsetMs),
        },
      });

      const routeIdMap = new Map<string, string>();
      for (const route of source.routes) {
        const created = await tx.route.create({
          data: {
            name: route.name,
            description: route.description,
            distance_meters: route.distance_meters,
            elevation_gain_meters: route.elevation_gain_meters,
            track_points: route.track_points ?? Prisma.DbNull,
            waypoints: route.waypoints ?? Prisma.DbNull,
            routeFileUrl: route.routeFileUrl,
            eventId: newEvent.id,
          },
        });
        routeIdMap.set(route.id, created.id);
      }

      for (const category of source.categories) {
        const created = await tx.category.create({
          data: {
            name: category.name,
            description: category.description,
            categoryGroup: category.categoryGroup,
            categoryGroupSortOrder: category.categoryGroupSortOrder,
            sortOrder: category.sortOrder,
            maxParticipants: category.maxParticipants,
            isOpen: category.isOpen,
            startTime: shiftDate(category.startTime, offsetMs),
            eventId: newEvent.id,
            routeId: category.routeId
              ? (routeIdMap.get(category.routeId) ?? null)
              : null,
            participantCount: 0,
            spotsRemaining: category.maxParticipants ?? null,
            isSoldOut: false,
          },
        });

        for (const price of category.prices) {
          await tx.categoryPrice.create({
            data: {
              name: price.name,
              amount: price.amount,
              currency: price.currency,
              endsAt: shiftDate(price.endsAt, offsetMs),
              categoryId: created.id,
            },
          });
        }
      }

      for (const row of source.prerequisites) {
        await tx.eventPrerequisite.create({
          data: {
            eventId: newEvent.id,
            prerequisiteEventId: row.prerequisiteEventId,
          },
        });
      }
      for (const row of source.prerequisiteOf) {
        await tx.eventPrerequisite.create({
          data: {
            eventId: row.eventId,
            prerequisiteEventId: newEvent.id,
          },
        });
      }

      return {eventId: newEvent.id};
    });

    return {success: true, eventId: result.eventId};
  });
